/* Gap decorations (specs/gap-decorations.md): css-gaps-1 rules for flex
 * and grid gaps, engine-painted as glyph runs. The utilities set the
 * real CSS properties (forward-compat; unknown ones drop harmlessly)
 * AND the --mw-* mirrors the engine reads — compiled by the consumer's
 * Tailwind build; inert as plain CSS.
 *
 * Width utilities set no style: Tailwind emits utilities alphabetically,
 * so a `solid` here would beat `rule-dashed` (probed) — the solid
 * default lives in the @property initial values instead. Color has no
 * registered initial (a <color> syntax can't take `currentColor`);
 * unset reads as "" = currentColor, like borders. */

@property --mw-rule-x-width {
  syntax: "<length>";
  inherits: false;
  initial-value: 0px;
}
@property --mw-rule-y-width {
  syntax: "<length>";
  inherits: false;
  initial-value: 0px;
}
@property --mw-rule-x-style {
  syntax: "solid | dashed | dotted | double";
  inherits: false;
  initial-value: solid;
}
@property --mw-rule-y-style {
  syntax: "solid | dashed | dotted | double";
  inherits: false;
  initial-value: solid;
}
@property --mw-rule-x-color {
  syntax: "*";
  inherits: false;
}
@property --mw-rule-y-color {
  syntax: "*";
  inherits: false;
}

@utility rule-x-* {
  --mw-rule-x-width: calc(--value("0", "2", "3", "4", integer) * 1px);
  --mw-rule-x-width: --value([*], --default(1px));
  column-rule-width: var(--mw-rule-x-width);
}
@utility rule-y-* {
  --mw-rule-y-width: calc(--value("0", "2", "3", "4", integer) * 1px);
  --mw-rule-y-width: --value([*], --default(1px));
  row-rule-width: var(--mw-rule-y-width);
}
/* Functional both-axes utility: integer/length values are widths, color
 * values are colors — non-matching declarations drop, Tailwind's
 * border-* disambiguation pattern. */
@utility rule-* {
  --mw-rule-x-width: calc(--value("0", "2", "3", "4", integer) * 1px);
  --mw-rule-x-width: --value([length], --default(1px));
  --mw-rule-y-width: calc(--value("0", "2", "3", "4", integer) * 1px);
  --mw-rule-y-width: --value([length], --default(1px));
  column-rule-width: var(--mw-rule-x-width);
  row-rule-width: var(--mw-rule-y-width);
  --mw-rule-x-color: --value(--color-*, [color]);
  --mw-rule-y-color: --value(--color-*, [color]);
  column-rule-color: var(--mw-rule-x-color);
  row-rule-color: var(--mw-rule-y-color);
}
@utility rule-solid {
  column-rule-style: solid;
  row-rule-style: solid;
  --mw-rule-x-style: solid;
  --mw-rule-y-style: solid;
}
@utility rule-dashed {
  column-rule-style: dashed;
  row-rule-style: dashed;
  --mw-rule-x-style: dashed;
  --mw-rule-y-style: dashed;
}
@utility rule-dotted {
  column-rule-style: dotted;
  row-rule-style: dotted;
  --mw-rule-x-style: dotted;
  --mw-rule-y-style: dotted;
}
@utility rule-double {
  column-rule-style: double;
  row-rule-style: double;
  --mw-rule-x-style: double;
  --mw-rule-y-style: double;
}

@property --mw-rule-break {
  syntax: "none | normal | intersection";
  inherits: false;
  initial-value: normal;
}
@property --mw-rule-inset {
  syntax: "<length> | overlap-join";
  inherits: false;
  initial-value: 0px;
}
@property --mw-rule-visibility-items {
  syntax: "normal | all | around | between";
  inherits: false;
  initial-value: normal;
}

@utility rule-break-none {
  rule-break: none;
  --mw-rule-break: none;
}
@utility rule-break-normal {
  rule-break: normal;
  --mw-rule-break: normal;
}
@utility rule-break-intersection {
  rule-break: intersection;
  --mw-rule-break: intersection;
}
@utility rule-inset-* {
  --mw-rule-inset: calc(--value("0", "1", "2", "3", "4", integer) * 1px);
  --mw-rule-inset: --value([*]);
  rule-inset: var(--mw-rule-inset);
}
@utility rule-inset-overlap-join {
  rule-inset: overlap-join;
  --mw-rule-inset: overlap-join;
}
@utility rule-visibility-all {
  rule-visibility-items: all;
  --mw-rule-visibility-items: all;
}
@utility rule-visibility-around {
  rule-visibility-items: around;
  --mw-rule-visibility-items: around;
}
@utility rule-visibility-between {
  rule-visibility-items: between;
  --mw-rule-visibility-items: between;
}

/* bg-clear: cut ancestor decoration glyphs out from under this element
 * WITHOUT painting a bg color. The engine fills the border-box with
 * plain spaces so anything below in the last-wins per-cell grid gets
 * wiped. `background-color: transparent` cascades over an earlier
 * `bg-red-500` in `bg-red-500 sm:bg-clear` so the browser-side
 * visible bg goes away too; the mirror is what the engine actually
 * reads. */
@utility bg-clear {
  background-color: transparent;
  --mw-bg-clear: 1;
}
@property --mw-bg-clear {
  syntax: "<integer>";
  inherits: false;
  initial-value: 0;
}

/* A native region (specs/native-regions.md): a box the grid sizes and
 * frames, its contents the browser's. */
@utility mw-native {
  --mw-native: 1;
}
@property --mw-native {
  syntax: "<integer>";
  inherits: false;
  initial-value: 0;
}

/* An image's colors (specs/images.md "Color"): a palette, matched by
 * color or lightness, levels per channel, and the dither of the last
 * reduction — inherited, so a theme sets them for its whole host, and
 * registered, so the engine reads each palette color computed. */
@utility image-palette-none {
  --mw-image-palette: none;
}
@utility image-posterize-* {
  --mw-image-levels: --value(integer, [integer]);
}
@utility image-dither-ordered {
  --mw-image-dither: ordered;
}
@utility image-dither-diffusion {
  --mw-image-dither: diffusion;
}
@utility image-dither-none {
  --mw-image-dither: none;
}
@property --mw-image-palette {
  syntax: "<color>+ | none";
  inherits: true;
  initial-value: none;
}
@property --mw-image-match {
  syntax: "color | lightness";
  inherits: true;
  initial-value: color;
}
@property --mw-image-levels {
  syntax: "<integer> | none";
  inherits: true;
  initial-value: none;
}
@property --mw-image-dither {
  syntax: "ordered | diffusion | none";
  inherits: true;
  initial-value: ordered;
}

/* Border glyph sets (specs/theming.md): utilities for the inherited
 * `--mw-border-glyphs` selector — which vocabulary border STYLES
 * render through. Arbitrary sets: `[--mw-border-glyphs:myset]`. */
@utility borders-default {
  --mw-border-glyphs: default;
}
@utility borders-rounded {
  --mw-border-glyphs: rounded;
}
@utility borders-ascii {
  --mw-border-glyphs: ascii;
}
@utility borders-single {
  --mw-border-glyphs: single;
}
@utility borders-blocks {
  --mw-border-glyphs: blocks;
}
@utility borders-cp437 {
  --mw-border-glyphs: cp437;
}

/* Scrollbar thickness in cells (specs/scrolling.md): CSS
 * scrollbar-width has no length form, so the size rides custom
 * properties — one per bar (`x` the horizontal bar's height, `y` the
 * vertical bar's width); the shorthand sets both, longhands win. */
@utility scrollbar-* {
  --mw-scrollbar-size-x: --value(integer);
  --mw-scrollbar-size-y: --value(integer);
}
@utility scrollbar-x-* {
  --mw-scrollbar-size-x: --value(integer);
}
@utility scrollbar-y-* {
  --mw-scrollbar-size-y: --value(integer);
}
@property --mw-scrollbar-size-x {
  syntax: "<integer>";
  inherits: false;
  initial-value: 1;
}
@property --mw-scrollbar-size-y {
  syntax: "<integer>";
  inherits: false;
  initial-value: 1;
}
/* Cells kept clear around the bars (specs/scrolling.md) as room for the
 * author's own arrow buttons: `-x` moves the vertical bar inward and
 * insets the horizontal one's ends, `-y` the mirror; two bars still
 * meet at one blank corner cell. */
@utility scrollbar-inset-* {
  --mw-scrollbar-inset-x: --value(integer);
  --mw-scrollbar-inset-y: --value(integer);
}
@utility scrollbar-inset-x-* {
  --mw-scrollbar-inset-x: --value(integer);
}
@utility scrollbar-inset-y-* {
  --mw-scrollbar-inset-y: --value(integer);
}
@property --mw-scrollbar-inset-x {
  syntax: "<integer>";
  inherits: false;
  initial-value: 0;
}
@property --mw-scrollbar-inset-y {
  syntax: "<integer>";
  inherits: false;
  initial-value: 0;
}
